import { encode as encodeBlurhash } from "blurhash";
import invariant from "invariant";
import { ArrowUpDown, Pencil, Trash2, Upload } from "lucide-react";
import type { Dispatch, FC } from "react";
import { useEffect, useRef, useState } from "react";
import type { CropperRef } from "react-advanced-cropper";
import { ImageRestriction } from "react-advanced-cropper";
import useMutation from "swr/mutation";
import { twMerge } from "tailwind-merge";

import { Button } from "~/components/button";
import {
	Credenza,
	CredenzaBody,
	CredenzaClose,
	CredenzaContent,
	CredenzaFooter,
	CredenzaHeader,
	CredenzaTitle,
	CredenzaTrigger,
} from "~/components/credenza";
import { ImageEditor } from "~/components/image-editor";
import type { IdOf } from "~/lib/id";
import { getImageUrl, uploadImage } from "~/lib/image";

export interface ImageUpload {
	value?: IdOf<"image">;
	width: number;
	height: number;
	blurhashOptions?: {
		xComponents: number;
		yComponents: number;
	};
	disabled?: boolean;
	onChange: Dispatch<{ id: IdOf<"image">; blurhash: string } | null>;
}

export const ImagePreview: FC<ImageUpload> = (props) => {
	const { value, width, height, blurhashOptions, disabled = false, onChange } = props;

	const [editorOpen, setEditorOpen] = useState(false);
	const [editorSource, setEditorSource] = useState(
		value
			? getImageUrl(value)
			: null
	);

	useEffect(() => {
		setEditorSource(
			value
				? getImageUrl(value)
				: null
		);
	}, [value]);

	const inputReference = useRef<HTMLInputElement>(null);
	const editorReference = useRef<CropperRef>(null);

	const { trigger: upload, isMutating: uploading } = useMutation("upload-image", async () => {
		invariant(!disabled, "`disabled` is true");

		const { current: editor } = editorReference;
		invariant(editor, "`editor` is null");

		const state = editor.getState();
		invariant(state, "`state` is null");

		const canvas = editor.getCanvas();
		invariant(canvas, "`canvas` is null");

		const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve));
		invariant(blob, "`blob` is null");

		const context = canvas.getContext("2d");
		invariant(context, "`context` is null");

		const { imageSize: { width, height } } = state;

		const { data } = context.getImageData(0, 0, width, height);

		const { xComponents = 3, yComponents = 3 } = blurhashOptions || {};
		const blurhash = encodeBlurhash(data, width, height, xComponents, yComponents);

		const id = await uploadImage(blob);
		return { id, blurhash };
	}, {
		onSuccess: onChange
	});

	function clearImage() {
		setEditorSource(null);
		onChange(null);
	}

	return (
		<Credenza open={editorOpen} onOpenChange={setEditorOpen}>
			<input
				accept="image/*"
				className="hidden"
				ref={inputReference}
				type="file"
				onChange={async (event) => {
					const file = event.target.files?.[0];
					if (!file) return;

					const objectUrl = URL.createObjectURL(file);

					setEditorSource(objectUrl);
					setEditorOpen(true);
				}}
			/>
			<CredenzaTrigger asChild>
				<button
					className="group flex w-full flex-col rounded border border-border shadow hover:shadow-lg disabled:pointer-events-none disabled:hidden disabled:brightness-75 md:disabled:flex"
					disabled={disabled}
					type="button"
					onClick={(event) => {
						if (value) return;

						event.stopPropagation();
						event.preventDefault();

						inputReference.current?.click();
					}}
				>
					<div className="flex justify-between border-b px-3 py-2 text-left text-sm">
						<span className="text-xs text-muted-foreground">
							Image
						</span>
						<span className="text-xs text-muted-foreground">
							{width === height
								? "Square"
								: width > height
									? "Landscape"
									: "Portrait"}
							{" "}
							(
							{width}
							x
							{height}
							)
						</span>
					</div>
					<div
						className={twMerge(
							"aspect-square w-full overflow-hidden rounded-b bg-contain bg-center bg-no-repeat",
							// "border-t-4 border-t-purple-600"
						)}
						style={{
							backgroundImage: value
								? `url(${getImageUrl(
									value,
								)})`
								: undefined
						}}
					>
						<div className="relative flex size-full items-center justify-center transition-all group-hover:backdrop-brightness-50">
							{ value
								? (
										<Pencil className="aspect-square w-4 opacity-0 transition-all group-hover:w-6 group-hover:opacity-100" />
									)
								: (
										<Upload className="aspect-square w-4 transition-all group-hover:w-6" />
									)}
						</div>
					</div>
				</button>
			</CredenzaTrigger>
			<CredenzaContent className="overflow-hidden md:max-w-2xl">
				<CredenzaHeader>
					<CredenzaTitle>Adjust image</CredenzaTitle>
				</CredenzaHeader>
				<CredenzaBody data-vaul-no-drag>
					<ImageEditor
						aspectRatio={width && height
							? {
									minimum: width / height,
									maximum: width / height
								}
							: undefined}
						// defaultCoordinates={{
						// 	top: 0,
						// 	left: 0,
						// 	height,
						// 	width
						// }}
						imageRestriction={ImageRestriction.fitArea}
						ref={editorReference}
						src={editorSource}
					/>
				</CredenzaBody>
				<CredenzaFooter>
					<div className="mr-auto flex gap-2">
						<CredenzaClose asChild>
							<Button
								className="gap-2"
								disabled={uploading}
								variant="ghost"
								onClick={() => clearImage()}
							>
								<Trash2 />
								Remove
							</Button>
						</CredenzaClose>
						<Button
							className="gap-2"
							disabled={uploading}
							variant="ghost"
							onClick={() => inputReference.current?.click()}
						>
							<ArrowUpDown />
							Replace
						</Button>
						<Button
							className="ml-auto"
							disabled={uploading}
							variant="ghost"
							onClick={() => editorReference.current?.reset()}
						>
							Reset
						</Button>
					</div>
					<Button
						autoFocus
						pending={uploading}
						onClick={async () => {
							await upload();
							setEditorOpen(false);
						}}
					>
						Continue
					</Button>
				</CredenzaFooter>
			</CredenzaContent>
		</Credenza>
	);
};
